import { App, Button, Input, Spin } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ApplicationState } from '../../../reducers';
import { intercomEvent } from '../../../utils/IntercomUtils';
import { isPDMUser } from '../../../utils/UserUtils';
import {
  createDynamicFieldList,
  deleteDynamicFieldList,
  exportDynamicFieldListExcelFile,
  fetchDynamicFieldLists,
  fetchUsedDynamicValueResources,
} from '../../../actions/settings/dynamicFieldList';
import DynamicFieldListTable from '../../../components/body/parentSettings/dynamicFieldList/DynamicFieldListTable';
import DynamicFieldListImportModal from '../../../components/body/parentSettings/dynamicFieldList/DynamicFieldListImportModal';
import AddDynamicFieldListPopover from '../../../components/body/parentSettings/dynamicFieldList/AddDynamicFieldListPopover';
import DynamicFieldListEditListRowsDrawer from '../../../components/body/parentSettings/dynamicFieldList/DynamicFieldListEditListRowsDrawer';
import { useAppDispatch } from '../../../store/ReduxStore';
import { AsyncDispatch } from '../../../../types/global';

const DynamicFieldListContainer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const { modal } = App.useApp();

  const { lists, fetchingLists, usedListIds, user } = useSelector((state: ApplicationState) => {
    return {
      lists: state.parent.dynamicFieldList.lists,
      fetchingLists: state.parent.dynamicFieldList.fetchingLists,
      usedListIds: state.parent.dynamicFieldList.usedListIds,
      user: state.user.user,
    };
  });

  const [selectedListName, setSelectedListName] = React.useState<string>('');
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [showEditDrawer, setShowEditDrawer] = React.useState(false);
  const [selectedList, setSelectedList] = React.useState<any>(null);
  const [keywords, setKeywords] = React.useState<string>('');

  const filteredLists = React.useMemo(() => {
    return lists.filter(list => list.name.toLowerCase().includes(keywords.toLowerCase()));
  }, [lists, keywords]);

  const pdmUser = React.useMemo(() => isPDMUser(user!), [user]);

  React.useEffect(() => {
    dispatch(fetchDynamicFieldLists());
    if (!pdmUser) {
      dispatch(fetchUsedDynamicValueResources());
    }
  }, [dispatch, pdmUser]);

  React.useEffect(() => {
    intercomEvent('viewed-dynamic-field-lists');
  }, []);

  const createList = (name: string, valueType: 'string' | 'numeric') => {
    return dispatch(createDynamicFieldList(name, valueType));
  };

  const deleteList = (id: number) => {
    const isListUsed = usedListIds.includes(id);

    modal.confirm({
      title: t('dynamicFieldList:deleteMessage'),
      content:
        isListUsed && pdmUser ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InfoCircleOutlined className="ant-info-tooltip pl-1" />
            <span>{t('dynamicFieldList:listInUseWarning')}</span>
          </div>
        ) : undefined,
      icon: <ExclamationCircleOutlined />,
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk() {
        return dispatch(deleteDynamicFieldList(id));
      },
    });
  };

  const importList = (name: string) => {
    setSelectedListName(name);
    setShowImportModal(true);
  };

  const exportList = (name: string) => {
    // @ts-ignore
    dispatch(exportDynamicFieldListExcelFile(name));
  };

  const editList = (list: any) => {
    setSelectedList(list);
    setShowEditDrawer(true);
  };

  const handleRowsUpdated = () => {
    dispatch(fetchDynamicFieldLists());
  };

  if (fetchingLists) return <Spin className="spinner-center" style={{ marginTop: '20px' }} />;

  return (
    <div className="dynamic-field-list__wrapper">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <Input.Search
            className="dynamic-field-list__search-input"
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
            placeholder={t('dynamicFieldList:searchPlaceholder')}
            allowClear
          />
          <div className="page-layout__top-bar__actions">
            <AddDynamicFieldListPopover createList={createList}>
              <Button type="primary">{t('dynamicFieldList:addList')}</Button>
            </AddDynamicFieldListPopover>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <DynamicFieldListTable
          fetchingLists={fetchingLists}
          lists={filteredLists}
          deleteList={deleteList}
          importList={importList}
          exportList={exportList}
          editList={editList}
          usedListIds={usedListIds}
          user={user!}
        />
      </div>

      <DynamicFieldListImportModal
        showImportModal={showImportModal}
        listName={selectedListName}
        handleOnCancel={() => {
          setShowImportModal(false);
          setSelectedListName('');
        }}
      />

      <DynamicFieldListEditListRowsDrawer
        open={showEditDrawer}
        list={selectedList}
        onClose={() => {
          setShowEditDrawer(false);
          setSelectedList(null);
        }}
        onRowsUpdated={handleRowsUpdated}
      />
    </div>
  );
};

export default DynamicFieldListContainer;
